<script lang="ts">
type PointerEventWithTarget = PointerEvent & { target?: HTMLElement }

/**
 * A single accordion element component.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/details
 * @see https://www.w3.org/WAI/ARIA/apg/patterns/accordion/examples/accordion/
 */
export default {
  props: {
    name: {
      type: String,
      required: true,
    },
    open: {
      type: Boolean,
      default: false,
    },
    summaryClass: {
      type: [String, Array<string>],
    },
    detailsClass: {
      type: [String, Array<string>],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    rounded: {
      type: Boolean,
      default: false,
    },
    lazyLoad: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['open', 'close', 'update:open'],
  data() {
    return {
      isOpen: this.open,
      hasBeenOpened: this.open, // Track if accordion has ever been opened
    }
  },
  mounted() {
    this.isOpen = this.open
    this.hasBeenOpened = this.open
  },
  methods: {
    handleToggle(event: Event) {
      const detailsElement = event.target as HTMLDetailsElement

      if (this.disabled) {
        return false
      }

      this.isOpen = detailsElement.open
      this.$emit('update:open', detailsElement.open)

      // Once opened, always mark as having been opened
      if (detailsElement.open) {
        this.hasBeenOpened = true
        this.$emit('open')
      } else {
        this.$emit('close')
      }
    },
  },
}
</script>

<template>
  <details
    ref="details"
    :name
    class="group relative h-fit border-slate-300"
    :open="isOpen ? true : undefined"
    @toggle="handleToggle"
  >
    <summary
      class="flex items-center justify-between gap-2 font-bold"
      :class="[
        summaryClass,
        disabled ? '' : 'hover:cursor-pointer',
        rounded ? 'rounded-md group-open:rounded-b-none' : '',
      ]"
    >
      <!-- @slot Used to place the title shown when opened or closed. -->
      <slot name="title" :props="{ isOpen }">
        <!-- @slot Used to place text when the accordion is open. -->
        <slot v-if="isOpen" name="open-title" />

        <!-- @slot Used to place text when the accordion is closed. -->
        <slot v-if="!isOpen" name="closed-title" />
      </slot>

      <!-- @slot Used to place the icon when the accordion is opened or closed. -->
      <slot name="icon" :props="{ isOpen }">
        <AppIcon
          v-if="!disabled"
          :icon="Icons.AccordionArrow"
          class="control-icon"
        />
      </slot>
    </summary>

    <template v-if="$slots.text">
      <article
        v-if="lazyLoad ? hasBeenOpened : true"
        :aria-expanded="isOpen"
        :class="[detailsClass, rounded ? 'rounded-b-md' : '']"
      >
        <div class="pb-4">
          <slot name="text" />
        </div>
      </article>
    </template>
  </details>
</template>

<style lang="scss" scoped>
details {
  :slotted(svg.control-icon),
  :deep(svg.control-icon) {
    @apply pointer-events-none ml-auto mr-2 w-fit select-none transition-transform duration-500 size-6;
  }

  &[open] > summary :slotted(svg.control-icon),
  &[open] > summary :deep(svg.control-icon) {
    @apply -rotate-180;
  }

  > summary :slotted(> *:first-child:not(svg)) {
    @apply w-full;
  }
}

// details:not(:only-of-type):not(:last-of-type) {
details:not(:last-of-type) {
  @apply border-b-[1px];

  // &:first-of-type {
  //   @apply border-t-[1px];
  // }
}

details > summary {
  list-style: none;
}

details > summary::marker, /* Latest Chrome, Edge, Firefox */
details > summary::-webkit-details-marker /* Safari */ {
  display: none;
}

// article {
//   @apply mt-2;
// }
</style>
